import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PurchaseOrderService } from "src/services";
import { QueryParamsMapper } from "src/mappers";
import { queryKeys } from "src/constants";
import { PurchaseOrder } from "src/types/database";
import {
  DateTimeRange,
  PurchaseOrderPaginationOptions,
  PaginatedPurchaseOrders,
  PurchaseOrdersFilters,
  UpdatePurchaseOrderOpsNoteParams
} from "src/types";

export const useUpdatePurchaseOrderOpsNote = (
  purchaseOrderService: PurchaseOrderService,
  dateTimeRange?: DateTimeRange,
  filter?: PurchaseOrdersFilters,
  paginationOptions?: PurchaseOrderPaginationOptions
) => {
  const queryClient = useQueryClient();
  const queryParams = QueryParamsMapper.getPurchaseOrdersQueryParams(dateTimeRange, filter, paginationOptions);
  const [lastVariables, setLastVariables] = useState<UpdatePurchaseOrderOpsNoteParams | null>(null);

  const updatePurchaseOrderInCache = (updatedPurchaseOrder: PurchaseOrder) => {
    if (dateTimeRange || filter || paginationOptions) {
      queryClient.setQueryData(
        queryKeys.paginatedPurchaseOrders(queryParams),
        (oldPaginatedData?: PaginatedPurchaseOrders) => {
          return {
            ...oldPaginatedData,
            items: oldPaginatedData?.items?.map((purchaseOrder) =>
              purchaseOrder.id === updatedPurchaseOrder.id ? updatedPurchaseOrder : purchaseOrder
            )
          };
        }
      );
    }

    queryClient.setQueryData(queryKeys.purchaseOrder(updatedPurchaseOrder.id), () => updatedPurchaseOrder);
  };

  const {
    mutate: updateOpsNote,
    isPending: isUpdating,
    error: updateError,
    reset: resetUpdate
  } = useMutation<PurchaseOrder, Error, UpdatePurchaseOrderOpsNoteParams>({
    mutationFn: ({ id, opsNote }) => purchaseOrderService.updatePurchaseOrderOpsNote(id, opsNote),
    onSuccess: updatePurchaseOrderInCache,
    onMutate: (variables) => {
      setLastVariables(variables);
    }
  });

  const retryUpdate = () => {
    if (lastVariables) {
      updateOpsNote(lastVariables);
    }
  };

  return {
    updateOpsNote,
    isUpdating,
    updateError,
    retryUpdate,
    resetUpdate
  };
};
